"use client";

import {
  Avatar,
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  MenuItem,
  MenuList,
  Popover,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { MouseEventHandler } from "react";
import { useState } from "react";
import { flatten } from "lodash";

export interface ProfileMenuItem {
  title: string;
  icon: React.ReactElement;
  action: () => void;
}

interface ProfileMenuProps {
  listItems: ProfileMenuItem[];
  firstName?: string;
  lastName?: string;
  profileTitle: string;
  profileSubtitle?: string;
  avatarImageUrl?: string;
  isLoading?: boolean;
  onMenuOpen?: () => void;
}

export const ProfileMenu = ({
  listItems,
  firstName = " ",
  lastName = " ",
  avatarImageUrl,
  profileTitle,
  profileSubtitle,
  isLoading = false,
  onMenuOpen,
}: ProfileMenuProps) => {
  const name = `${firstName} ${lastName}`;
  const initial = `${firstName[0]?.toLocaleUpperCase()}${lastName[0]?.toLocaleUpperCase()}`;

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchor);

  const onAvatarButtonClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setMenuAnchor(event.currentTarget);
    onMenuOpen?.();
  };

  const onMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <Button
        aria-controls={isMenuOpen ? "profile-menu" : undefined}
        aria-expanded={isMenuOpen ? "true" : undefined}
        aria-haspopup="true"
        data-cy="profile-button"
        disabled={isLoading}
        endIcon={
          <ExpandMoreIcon
            sx={(theme) => ({
              color: "#262626",
              ...theme.applyStyles("dark", { color: "white" }),
            })}
          />
        }
        id="profile-button"
        onClick={onAvatarButtonClick}
        variant="text"
        size="small"
      >
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Avatar src={avatarImageUrl}>{initial}</Avatar>
        )}
      </Button>
      <Popover
        anchorEl={menuAnchor}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        classes={{
          paper: "min-w-[240px] rounded-2xl",
        }}
        data-cy="avatar=topbar-btn"
        id="profile-menu"
        onClose={onMenuClose}
        open={isMenuOpen}
      >
        <section className="h-42 flex flex-col items-center pb-4 pt-6">
          <Avatar
            alt={name}
            className="h-24 w-24 text-4xl"
            src={avatarImageUrl}
          >
            {initial}
          </Avatar>
          <div className="flex flex-col items-center pt-4">
            <span className="body-l-strong">{profileTitle}</span>
            {profileSubtitle ? (
              <span className="body-m">{profileSubtitle}</span>
            ) : null}
          </div>
        </section>
        <MenuList aria-labelledby="profile-button">
          {flatten(
            listItems.map(({ title, action, icon }) => [
              <Divider
                classes={{
                  root: "border-gray-300",
                }}
                key={`${title}-divider`}
                style={{
                  margin: "4px 16px",
                }}
              />,
              <MenuItem
                classes={{
                  root: "button flex h-16 cursor-pointer items-center justify-start gap-2 border-t border-gray-300",
                }}
                key={title}
                onClick={() => {
                  action();
                  onMenuClose();
                }}
              >
                <ListItemIcon>{icon}</ListItemIcon>
                <Typography noWrap>{title}</Typography>
              </MenuItem>,
            ])
          )}
        </MenuList>
      </Popover>
    </>
  );
};
